import {cn} from "../../ui/lib/utils";
import {DateTimePickerRange} from "../../ui/DateTimeSelector";
import {MultiMetoroMetricsChart, MultiMetoroMetricsChartProps} from "../../Charts/MultiMetoroMetricsCharts";
import {ChartType} from "../../Charts/MetoroChart";
import React, {useEffect} from "react";
import {useSelector} from "react-redux";
import timerange from "../../../store/reducers/timerange";
import {GaugeChart} from "../../Charts/GaugeChart";
import {MetricQuerySelectorPanel} from "./MultiMetricSelector";
import {ReduceOption, ReduceOptions, GaugeWidget} from "../internalwidgets";
import {RuntimeVariable} from "../Dashboard";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuTrigger
} from "../../ui/dropdown-menu";
import {ChevronDownIcon, Cross2Icon} from "@radix-ui/react-icons";
import {Input} from "../../ui/input";
import {Label} from "../../ui/label";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "../../ui/select";
import {Switch} from "../../ui/switch";
import {Alert} from "../../ui/alert";
import {AlertCircle, XIcon} from "lucide-react";
import {UnitOverrideSelector} from "../../Input/UnitOverrideSelector";
import {MetricType} from "../../../pages/MetricsTest";
import {RateFunctionType} from "./MetricSelector";
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger
} from "../../ui/tooltip";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from "../../ui/dialog";
import {Button} from "../../ui/button";
import {ReduceOptionSelector} from "./StatSelectorPanel";

const empty: string[] = []

export function GaugeSelectorPanel(props: {
    widget: GaugeWidget,
    setIsDialogOpen: React.Dispatch<React.SetStateAction<boolean>>
    runtimeVariables: RuntimeVariable[]
    onSave?: (widget: GaugeWidget) => void
    setLabel: React.Dispatch<React.SetStateAction<string>>
    label: string
}) {
    const timeRange = useSelector(timerange.selectors.getTimeRange)
    const [showConfirmDialog, setShowConfirmDialog] = React.useState<boolean>(false);

    const [chartProps, setChartProps] = React.useState<MultiMetoroMetricsChartProps>({
        startTime: Math.floor(timeRange.getStartEnd()[0].getTime() / 1000),
        endTime: Math.floor(timeRange.getStartEnd()[1].getTime() / 1000),
        type: ChartType.Line,
        variables: props.runtimeVariables,
        hideLegend: false,
        metricSpecifiers: props.widget.metricSpecifiers || [
            {
                metricType: MetricType.Metric,
                metricName: "container_resources_cpu_usage_seconds_total",
                splits: [],
                filters: new Map(),
                excludeFilters: new Map(),
                functions: [{
                    id: "1",
                    functionType: RateFunctionType.PerSecond
                }],
                bucketSize: 60,
                aggregation: "min",
            },
        ],
        formulas: props.widget.formulas || [],
        metoroQLQueries: props.widget.metoroQLQueries || [],
        isMetoroQlSelector: props.widget.metoroQLQueries && props.widget.metoroQLQueries.length > 0,
    });
    const [reduceOption, setReduceOption] = React.useState<ReduceOption>(props.widget.reduceOption);
    const [min, setMin] = React.useState<number>(props.widget.min || 0);
    const [max, setMax] = React.useState<number>(props.widget.max || 100);
    const [precision, setPrecision] = React.useState<number>(props.widget.precision ?? 2);
    const [showAbsoluteValue, setShowAbsoluteValue] = React.useState<boolean>(props.widget.showAbsoluteValue ?? false);
    const [unitOverride, setUnitOverride] = React.useState<string | undefined>(props.widget.unitOverride);
    const [validationError, setValidationError] = React.useState<string | undefined>();

    // Display values for inputs (updated immediately while typing)
    const [labelDisplay, setLabelDisplay] = React.useState<string>(props.widget.label || "");
    const [minDisplay, setMinDisplay] = React.useState<string>((props.widget.min || 0).toString());
    const [maxDisplay, setMaxDisplay] = React.useState<string>((props.widget.max || 100).toString());

    // Update state when widget prop changes
    useEffect(() => {
        setChartProps(prev => ({
            ...prev,
            metricSpecifiers: props.widget.metricSpecifiers || [],
            formulas: props.widget.formulas || [],
            metoroQLQueries: props.widget.metoroQLQueries || [],
            isMetoroQlSelector: props.widget.metoroQLQueries && props.widget.metoroQLQueries.length > 0,
            variables: props.runtimeVariables,
        }));
    }, [props.widget.metricSpecifiers, props.widget.formulas, props.widget.metoroQLQueries, props.runtimeVariables]);

    // Update both actual and display values when widget props change
    useEffect(() => {
        const newLabel = props.widget.label || "";
        props.setLabel(newLabel);
        setLabelDisplay(newLabel);
    }, [props.widget.label]);

    useEffect(() => {
        const newMin = props.widget.min || 0;
        setMin(newMin);
        setMinDisplay(newMin.toString());
    }, [props.widget.min]);

    useEffect(() => {
        const newMax = props.widget.max || 100;
        setMax(newMax);
        setMaxDisplay(newMax.toString());
    }, [props.widget.max]);

    useEffect(() => {
        setPrecision(props.widget.precision ?? 2);
    }, [props.widget.precision]);

    useEffect(() => {
        setReduceOption(props.widget.reduceOption);
    }, [props.widget.reduceOption]);

    useEffect(() => {
        setShowAbsoluteValue(props.widget.showAbsoluteValue ?? false);
    }, [props.widget.showAbsoluteValue]);

    useEffect(() => {
        setUnitOverride(props.widget.unitOverride);
    }, [props.widget.unitOverride]);

    // Validate that we have at least one metric, formula, or MetoroQL query if metricSpecifiers are provided
    useEffect(() => {
        if (props.widget.metricSpecifiers && props.widget.metricSpecifiers.length > 0) {
            const visibleMetrics = chartProps.metricSpecifiers.filter(m => !m.shouldNotReturn).length;
            const formulaCount = chartProps.formulas?.length || 0;
            const metoroQLCount = chartProps.metoroQLQueries?.filter(q => !q.shouldNotReturn)?.length || 0;

            if (visibleMetrics + formulaCount + metoroQLCount === 0) {
                setValidationError("You must have either one visible metric, one formula, or one MetoroQL query for the gauge");
            } else if (visibleMetrics + formulaCount + metoroQLCount > 1) {
                setValidationError("You can only have one visible metric, formula, or MetoroQL query for the gauge. Please mute other metrics by clicking on the variable icon in the metric query panel.");
            } else {
                setValidationError(undefined);
            }
        } else {
            setValidationError(undefined);
        }
    }, [chartProps.metricSpecifiers, chartProps.formulas, chartProps.metoroQLQueries, props.widget.metricSpecifiers]);

    const handleClose = () => {
        setShowConfirmDialog(true);
    };

    const handleConfirmClose = () => {
        setShowConfirmDialog(false);
        props.setIsDialogOpen(false);
    };

    const handleCancelClose = () => {
        setShowConfirmDialog(false);
    };

    const handleSave = () => {
        if (validationError) {
            return;
        }

        const updatedWidget: GaugeWidget = {
            ...props.widget,
            widgetType: "Gauge",
            metricSpecifiers: chartProps.metricSpecifiers,
            formulas: chartProps.formulas,
            metoroQLQueries: chartProps.metoroQLQueries,
            reduceOption: reduceOption,
            label: props.label,
            min: min,
            max: max,
            precision: precision,
            showAbsoluteValue: showAbsoluteValue,
            unitOverride: unitOverride,
        };

        if (props.onSave) {
            props.onSave(updatedWidget);
        }
        props.setIsDialogOpen(false);
    };

    // Handlers for updating actual values (used by chart)
    const handleLabelUpdate = (value: string) => {
        props.setLabel(value);
    };

    const handleMinUpdate = (value: string) => {
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
            setMin(numValue);
        }
    };

    const handleMaxUpdate = (value: string) => {
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
            setMax(numValue);
        }
    };

    // Handle Enter key press
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, updateHandler: (value: string) => void) => {
        if (e.key === 'Enter') {
            updateHandler((e.target as HTMLInputElement).value);
        }
    };



    return <div
        className={cn("flex flex-col grow rounded bg-backgrounddark overflow-y-auto h-full border gap-4")}>
        <div className={"flex-none flex justify-between border-b h-[32px]"}>
            <div className={"flex flex-col justify-center text-textdark ml-2 items-center text-center"}>
                Gauge Chart Builder
            </div>
            <div className={"flex"}>
                <DateTimePickerRange/>
                <div
                    className={"text-textmedium border border-red-500 bg-red-500/20 rounded flex items-center justify-center pl-1 m-1 hover:cursor-pointer hover:text-textlight"}
                    onClick={handleClose}>
                    Cancel <XIcon className={"w-5 h-5"}/>
                </div>
            </div>
        </div>

        {/* Add confirmation dialog */}
        <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
            <DialogContent className="sm:max-w-[525px] bg-backgroundmedium">
                <DialogHeader>
                    <DialogTitle>Close Editor?</DialogTitle>
                    <DialogDescription className={"text-textmedium"}>
                        Are you sure you want to close the editor? Any changes will be discarded.
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button onClick={handleCancelClose}
                            className={"text-textmedium border rounded border-primary bg-primarytransparent"}>
                        Back to Editor
                    </Button>
                    <Button onClick={handleConfirmClose}
                            className={"text-textmedium border rounded border-amber-500 bg-amber-500/10"}>
                        Close Without Saving
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>

        <div className={"gap-6 p-4 grid grid-cols-2 min-h-[360px]"}>
            <div className={"flex flex-col justify-center h-full overflow-y-auto"}>
                <GaugeChart
                    // label={label}
                    min={min || 0}
                    max={max || 100}
                    precision={precision || 2}
                    showAbsoluteValue={showAbsoluteValue || false}
                    unitOverride={unitOverride}
                    className="w-full h-full"
                    metricSpecifiers={chartProps.metricSpecifiers}
                    formulas={chartProps.formulas}
                    metoroQLQueries={chartProps.metoroQLQueries}
                    runtimeVariables={chartProps.variables}
                    reduceOption={reduceOption || "avg"}
                    environments={empty}
                />
            </div>
            <MultiMetoroMetricsChart
                {...chartProps}
                variables={props.runtimeVariables}
                startTime={Math.floor(timeRange.getStartEnd()[0].getTime() / 1000)}
                endTime={Math.floor(timeRange.getStartEnd()[1].getTime() / 1000)}
                hideLegend={false}
                className={"flex flex-grow"}
                isAggregateSelector={true}
            />
        </div>
        <div className={"p-4 flex flex-col gap-4"}>
            <MetricQuerySelectorPanel variables={props.runtimeVariables}
                                      hideAutoBucket={true}
                                      chartProps={chartProps}
                                      setChartProps={setChartProps}
                                      disableVisualEditButton={true}
                                      isMetoroQlSelector={chartProps.isMetoroQlSelector}
                                      metricSelectorDisplaySettings={{
                                          showFormulaSelector: true,
                                          showTypeSelector: true,
                                          showMetricName: true,
                                          showGroupBy: true,
                                          showAggregation: true,
                                          showFilters: true,
                                          showJsonPath: true,
                                          showConfigureTitle: true,
                                          showAddTransformButton: true,
                                      }}
                                      stepNumber={1}
            />
            {validationError && (
                <Alert className="mt-2 bg-red-400/10 border border-red-400 text-red-400">
                    <AlertCircle className="h-4 w-4"/>
                    <span className="ml-2">{validationError}</span>
                </Alert>
            )}
            <ReduceOptionSelector
                reduceOption={reduceOption}
                setReduceOption={setReduceOption}
                stepNumber={2}
            />

            <div className={"flex flex-col gap-4"}>
                <div className={"flex justify-start gap-2"}>
                    <div
                        className={"h-[36px] w-[36px] border border-primary bg-primarytransparent text-textlight flex flex-col justify-center text-center font-semibold rounded"}>
                        3
                    </div>
                    <div className={"text-lg flex flex-col justify-center text-center text-textlight"}>
                        Configure Gauge Settings
                    </div>
                </div>
                <div className={"flex flex-col gap-4"}>
                    <div className={"flex items-center gap-4"}>
                        <Label className="text-textmedium min-w-[100px]">Label</Label>
                        <Input
                            value={labelDisplay}
                            onChange={(e) => setLabelDisplay(e.target.value)}
                            onBlur={(e) => handleLabelUpdate(e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, handleLabelUpdate)}
                            placeholder="Enter label"
                            className={"h-12 w-1/3 focus:ring-0 focus:border-border focus:ring-border text-textlight border border-border shadow-none"}
                        />
                    </div>
                    <div className={"flex items-center gap-4"}>
                        <Label className="text-textmedium min-w-[100px]">Min Value</Label>
                        <Input
                            type="number"
                            value={minDisplay}
                            onChange={(e) => setMinDisplay(e.target.value)}
                            onBlur={(e) => handleMinUpdate(e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, handleMinUpdate)}
                            className={"h-12 w-1/3 focus:ring-0 focus:border-border focus:ring-border text-textlight border border-border shadow-none"}
                        />
                    </div>
                    <div className={"flex items-center gap-4"}>
                        <Label className="text-textmedium min-w-[100px]">Max Value</Label>
                        <Input
                            type="number"
                            value={maxDisplay}
                            onChange={(e) => setMaxDisplay(e.target.value)}
                            onBlur={(e) => handleMaxUpdate(e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, handleMaxUpdate)}
                            className={"h-12 w-1/3 focus:ring-0 focus:border-border focus:ring-border text-textlight border border-border shadow-none"}
                        />
                    </div>
                    <div className={"flex items-center gap-4"}>
                        <Label className="text-textmedium min-w-[100px]">Precision</Label>
                        <Select
                            value={precision.toString()}
                            onValueChange={(value) => {
                                // Also update precision immediately when the select value changes
                                // since this is a direct user action (similar to blur/enter)
                                const precisionValue = parseInt(value);
                                if (!isNaN(precisionValue)) {
                                    setPrecision(precisionValue);
                                }
                            }}
                        >
                            <SelectTrigger
                                className="w-[100px] bg-backgrounddark border border-border rounded text-textmedium">
                                <SelectValue
                                    placeholder="Decimal points"/>
                            </SelectTrigger>
                            <SelectContent className={"bg-backgrounddark text-textmedium"}>
                                {[0, 1, 2, 3, 4].map((value) => (
                                    <SelectItem
                                        key={value}
                                        className={"hover:bg-secondarytransparenter hover:border-secondary hover:border"}
                                        value={value.toString()}
                                    >
                                        {value}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className={"flex items-center gap-4"}>
                        <Label className="text-textmedium min-w-[100px]">Show Absolute Value</Label>
                        <Switch
                            checked={showAbsoluteValue}
                            onCheckedChange={setShowAbsoluteValue}
                            className="data-[state=checked]:bg-primary"
                        />
                        <span className="text-textdark text-sm">
                            {showAbsoluteValue ? "Display raw value" : "Display percentage"}
                        </span>
                    </div>
                    {showAbsoluteValue && (
                        <div className={"flex items-center gap-4"}>
                            <Label className="text-textmedium min-w-[100px]">Unit Override</Label>
                            <div className="w-1/3">
                                <UnitOverrideSelector
                                    value={unitOverride}
                                    onChange={setUnitOverride}
                                    label=""
                                    className=""
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className={"flex justify-center items"}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <div
                            onClick={validationError ? undefined : handleSave}
                            className={cn(
                                "flex grow text-center items-center justify-center border p-2 rounded",
                                validationError
                                    ? "bg-backgrounddark border-border text-textdark cursor-not-allowed"
                                    : "bg-primarytransparent border-primary text-textlight hover:bg-primarydark cursor-pointer"
                            )}
                        >
                            Save
                        </div>
                    </TooltipTrigger>
                    {validationError && (
                        <TooltipContent className="bg-backgrounddark text-textmedium border border-border">
                            {validationError}
                        </TooltipContent>
                    )}
                </Tooltip>
            </div>
        </div>
    </div>
}
